"use client";

import { ForwardedRef, forwardRef, TextareaHTMLAttributes, useCallback, useState } from "react";

import { cn } from "@/lib/utils";

// Temporary fix until "field-sizing: content;" CSS is not supported in every mainstream browser
// Currently not supported in Safari and Firefox
type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  containerClassName?: string;
  useContentHeight?: boolean;
};

function ForwardedTextarea(
  { className, containerClassName, style, useContentHeight, ...props }: TextareaProps,
  ref: ForwardedRef<HTMLTextAreaElement>
) {
  const [contentHeight, setContentHeight] = useState(0);

  const spanRef = useCallback(
    (node: HTMLSpanElement | null) => {
      if (useContentHeight && node) {
        setContentHeight(node.getBoundingClientRect().height);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useContentHeight, props.value]
  );

  return (
    <div className={cn("relative", containerClassName)}>
      {useContentHeight && (
        <span
          ref={spanRef}
          className={cn(
            "w-full invisible absolute text-sm pointer-events-none whitespace-pre-wrap break-words px-3 py-2 border -z-50",
            className
          )}
        >
          {props.value}
        </span>
      )}

      <textarea
        ref={ref}
        className={cn(
          "flex min-h-[60px] w-full bg-white text-sm text-gray-800 border border-gray-600 rounded-lg px-3 py-2 outline-primary-300",
          "hover:border-primary-300 placeholder:text-gray-600 disabled:hover:border-gray-600 disabled:cursor-not-allowed disabled:opacity-75",
          className
        )}
        style={useContentHeight ? { ...style, height: contentHeight } : style}
        {...props}
      />
    </div>
  );
}

const Textarea = forwardRef(ForwardedTextarea);
export default Textarea;
